import { defineStore } from "pinia";
import disciplinePackagesApi, {
    ConfiguredDisciplinePackage,
    DisciplineMetadataMap,
    DisciplineType,
    EligibleCategory,
    ScoreboardSettings,
    ScoreboardTypeSettingsMap,
} from "../Api/disciplinePackages";
import bracketsApi, { BracketTypeConstraintWithTranslationKey } from "../Api/brackets";
import { BracketTypeConstraint } from "../Brackets/configurationTemplates";
import { BracketTypeEnum } from "../Common/types";
import { handleError } from "../Common/errorHandler";
import { successToast } from "../Common/toasts";
import { translate } from "../Common/translations";
import { ScoreboardType } from "../Scoreboard/Common/types";
import { normalizeScoreboardSettings, resetScoreboardSettingsForType } from "./ScoreboardSettings/scoreboardSettingsAdapters";

function feedBracketConstraintWithDefaultSettings(c: BracketTypeConstraint): BracketTypeConstraint {
    if (c.bracketType === BracketTypeEnum.Kata && !c.bracketTypeSettings) {
        return { ...c, bracketTypeSettings: { numberOfPerformances: 1 } };
    }
    if (c.bracketType === BracketTypeEnum.RepechageByFinalists && !c.bracketTypeSettings) {
        return { ...c, bracketTypeSettings: { numberOfThirdPlaces: 2, pullDepth: null } };
    }
    return c;
}

/** Backend-provided ranges (allBracketTypes) are the single source of truth for which type supports which range. */
function constraintFitsRange(c: BracketTypeConstraint, supportedTypes: BracketTypeConstraintWithTranslationKey[]): boolean {
    return (
        Number.isInteger(c.lowerLimit) &&
        Number.isInteger(c.upperLimit) &&
        c.lowerLimit <= c.upperLimit &&
        supportedTypes.some(t => t.bracketType === c.bracketType && t.lowerLimit <= c.lowerLimit && t.upperLimit >= c.upperLimit)
    );
}

export type DisciplineDefaultConfig = {
    scoreboardType: ScoreboardType | null;
    scoreboardSettings: ScoreboardSettings;
    bracketConstraints: BracketTypeConstraint[];
    bracketTypeSettings: object; //TODO Not yet supported.
};

export type DisciplineOverrideConfig = {
    id: number | null;
    name: string;
    bracketConstraints: BracketTypeConstraint[];
    bracketTypeSettings: object; //TODO Not yet supported.
    parameterIds: number[];
    scoreboardType: ScoreboardType | null;
    scoreboardSettings: ScoreboardSettings | null;
};

export const useDisciplinePackagesStore = defineStore("disciplinePackages", {
    state: () => ({
        eventId: 0,
        loading: true,
        saving: false,
        packages: [] as ConfiguredDisciplinePackage[],
        uncoveredCategories: 0,
        selectedDiscipline: null as DisciplineType | null,
        allBracketTypes: [] as BracketTypeConstraintWithTranslationKey[],
        metadata: null as DisciplineMetadataMap | null,
        allScoreboardSettings: {} as ScoreboardTypeSettingsMap,
        disciplineDefaultConfig: {
            scoreboardType: null,
            scoreboardSettings: {},
            bracketConstraints: [] as BracketTypeConstraint[],
            bracketTypeSettings: {},
        } as DisciplineDefaultConfig,
        activeTabIndex: null as number | null,
        disciplineOverrideConfigs: [] as DisciplineOverrideConfig[],
        eligibleCategories: [] as EligibleCategory[],
        eligibleCategoriesLoading: false,
    }),

    getters: {
        selectedPackage: state => {
            if (!state.selectedDiscipline) {
                return null;
            }
            return state.packages.find(p => p.discipline === state.selectedDiscipline) ?? null;
        },
        orphanedPackages: state => state.packages.filter(p => p.isOrphaned),
        nonOrphanedPackages: state => state.packages.filter(p => !p.isOrphaned),
        unconfiguredCount: state => state.packages.filter(p => !p.isConfigured && !p.isOrphaned).length,
        currentAllowedBracketTypes: state => {
            if (!state.selectedDiscipline || !state.metadata) {
                return [] as BracketTypeConstraintWithTranslationKey[];
            }
            const allowed = state.metadata[state.selectedDiscipline]?.allowedBracketTypes ?? [];
            return state.allBracketTypes.filter(bt => allowed.includes(bt.bracketType));
        },
        /** Scoreboard types available for the currently selected discipline. */
        currentScoreboardTypes: state => {
            if (!state.selectedDiscipline || !state.metadata) {
                return [] as string[];
            }
            return state.metadata[state.selectedDiscipline]?.scoreboardTypes ?? [];
        },
        /** Configurable setting keys available for the currently selected scoreboard type. */
        selectedScoreboardSettings: state => {
            let activeType = state.disciplineDefaultConfig.scoreboardType;
            if (state.activeTabIndex !== null) {
                const scoreboardType = state.disciplineOverrideConfigs[state.activeTabIndex]?.scoreboardType;
                if (scoreboardType !== null && scoreboardType !== undefined) {
                    activeType = scoreboardType;
                }
            }
            return activeType ? (state.allScoreboardSettings[activeType] ?? {}) : {};
        },
        /** Active bracket constraints — from override or default depending on active tab. */
        activeConstraints: state => {
            if (state.activeTabIndex === null) {
                return state.disciplineDefaultConfig.bracketConstraints;
            }
            return state.disciplineOverrideConfigs[state.activeTabIndex]?.bracketConstraints ?? [];
        },
        /** Active bracket type settings — from override or default depending on active tab. */
        activeBracketTypeSettings: state => {
            if (state.activeTabIndex === null) {
                return state.disciplineDefaultConfig.bracketTypeSettings;
            }
            return (
                state.disciplineOverrideConfigs[state.activeTabIndex]?.bracketTypeSettings ??
                {
                    //TODO: Later we will need to provide here supported settings for bracket types.
                }
            );
        },
        /** Default bracket type value for competitor counts not covered by any configured range. */
        currentDefaultBracketType: state => {
            if (!state.selectedDiscipline || !state.metadata) {
                return null;
            }
            return state.metadata[state.selectedDiscipline]?.defaultBracketType ?? null;
        },
        /** Whether the selected discipline uses bracket ranges (multiple allowed bracket types). */
        usesBracketRanges: state => {
            if (!state.selectedDiscipline || !state.metadata) {
                return true;
            }
            return (state.metadata[state.selectedDiscipline]?.allowedBracketTypes?.length ?? 0) > 1;
        },
        /** Whether any constraint across all configs has an unresolved bracket type or a type that does not support its range. */
        hasInvalidBracketConstraints: state => {
            const allConstraints = [
                ...state.disciplineDefaultConfig.bracketConstraints,
                ...state.disciplineOverrideConfigs.flatMap(o => o.bracketConstraints),
            ];
            return allConstraints.some(c => !constraintFitsRange(c, state.allBracketTypes));
        },
        /** Whether the bracket section should be shown at all for the selected discipline. */
        showsBracketSection: state => {
            if (!state.selectedDiscipline || !state.metadata) {
                return true;
            }
            return (state.metadata[state.selectedDiscipline]?.allowedBracketTypes?.length ?? 0) >= 1;
        },
        /** Translation key for the default bracket type name. */
        currentDefaultBracketTypeTranslationKey: state => {
            const defaultType = state.metadata?.[state.selectedDiscipline ?? ""]?.defaultBracketType;
            if (!defaultType) {
                return null;
            }
            return state.allBracketTypes.find(bt => bt.bracketType === defaultType)?.name ?? null;
        },
        /** Active scoreboard type — override's if non-null, otherwise default form state. */
        activeScoreboardType: state => {
            if (state.activeTabIndex !== null) {
                const v = state.disciplineOverrideConfigs[state.activeTabIndex]?.scoreboardType;
                if (v !== null && v !== undefined) {
                    return v;
                }
            }
            return state.disciplineDefaultConfig.scoreboardType;
        },
        /** Active scoreboard settings — override's if non-null, otherwise default form state. */
        activeScoreboardSettings: state => {
            if (state.activeTabIndex !== null) {
                const s = state.disciplineOverrideConfigs[state.activeTabIndex]?.scoreboardSettings;
                if (s !== null && s !== undefined) {
                    return s;
                }
            }
            return state.disciplineDefaultConfig.scoreboardSettings;
        },
        /** Eligible categories not yet assigned to any other override (for new/current override selection). */
        categoriesAvailableForNewOverride: state => {
            const usedIds = new Set<number>();
            state.disciplineOverrideConfigs.forEach((override, idx) => {
                if (idx !== state.activeTabIndex) {
                    override.parameterIds.forEach(id => usedIds.add(id));
                }
            });
            return state.eligibleCategories.filter(cat => !usedIds.has(cat.id));
        },
    },

    actions: {
        init(eventId: number): void {
            this.eventId = eventId;
            this.loading = true;
            Promise.all([
                disciplinePackagesApi.getDisciplinePackages(eventId),
                disciplinePackagesApi.getDisciplineMetadata(),
                bracketsApi.getBracketTypes(eventId),
            ])
                .then(([pkgsResponse, metadataResponse, bracketTypes]) => {
                    this.packages = pkgsResponse.disciplines;
                    this.uncoveredCategories = pkgsResponse.uncoveredCategories;
                    this.metadata = metadataResponse.disciplines;
                    this.allScoreboardSettings = metadataResponse.scoreboardSettings;
                    this.allBracketTypes = bracketTypes;
                    const nonOrphaned = pkgsResponse.disciplines.filter(p => !p.isOrphaned);
                    if (nonOrphaned.length > 0) {
                        const firstUnconfigured = nonOrphaned.find(p => !p.isConfigured);
                        this.selectDiscipline(firstUnconfigured ?? nonOrphaned[0]);
                    }
                })
                .catch(handleError)
                .finally(() => {
                    this.loading = false;
                });
        },

        deletePackage(discipline: DisciplineType): void {
            const name = translate(`discipline_packages.discipline.${discipline}`);
            if (!window.confirm(translate("discipline_packages.orphaned_remove_confirm", false, [name]))) {
                return;
            }
            disciplinePackagesApi
                .deleteDisciplinePackage(this.eventId, discipline)
                .then(() => {
                    this.packages = this.packages.filter(p => p.discipline !== discipline);
                    if (this.selectedDiscipline === discipline) {
                        this.selectedDiscipline = null;
                    }
                    successToast(translate("discipline_packages.orphaned_removed"));
                })
                .catch(handleError);
        },

        selectDiscipline(pkg: ConfiguredDisciplinePackage): void {
            this.selectedDiscipline = pkg.discipline;
            this.activeTabIndex = null;
            if (!this.metadata) {
                return;
            }

            const meta = this.metadata[pkg.discipline];
            const selectedType = (pkg.isConfigured ? pkg.scoreboardType : meta.defaults.scoreboardType) as ScoreboardType;

            this.disciplineDefaultConfig = {
                scoreboardType: selectedType,
                scoreboardSettings: pkg.isConfigured
                    ? normalizeScoreboardSettings(selectedType, pkg.scoreboardSettings)
                    : resetScoreboardSettingsForType(selectedType),
                bracketConstraints: pkg.isConfigured
                    ? JSON.parse(JSON.stringify(pkg.bracketConstraints)).map(feedBracketConstraintWithDefaultSettings)
                    : [],
                bracketTypeSettings: {},
            };

            this.disciplineOverrideConfigs = (pkg.parameterOverrides ?? []).map(o => ({
                id: o.id,
                name: o.name ?? "",
                bracketConstraints: o.bracketConstraints
                    ? JSON.parse(JSON.stringify(o.bracketConstraints)).map(feedBracketConstraintWithDefaultSettings)
                    : JSON.parse(JSON.stringify(this.disciplineDefaultConfig.bracketConstraints)),
                bracketTypeSettings: {},
                parameterIds: [...(o.parameterIds ?? [])],
                scoreboardType: (o.scoreboardType as ScoreboardType) ?? null,
                scoreboardSettings: o.scoreboardType
                    ? normalizeScoreboardSettings(o.scoreboardType as ScoreboardType, o.scoreboardSettings)
                    : null,
            }));

            this.loadEligibleCategories();
        },

        loadEligibleCategories(): void {
            if (!this.selectedDiscipline) {
                return;
            }
            this.eligibleCategoriesLoading = true;
            disciplinePackagesApi
                .getEligibleCategories(this.eventId, this.selectedDiscipline)
                .then(categories => {
                    this.eligibleCategories = categories;
                })
                .catch(handleError)
                .finally(() => {
                    this.eligibleCategoriesLoading = false;
                });
        },

        selectTab(index: number | null): void {
            this.activeTabIndex = index;
        },

        addOverride(): void {
            this.disciplineOverrideConfigs.push({
                id: null,
                name: "",
                bracketConstraints: JSON.parse(JSON.stringify(this.disciplineDefaultConfig.bracketConstraints)),
                bracketTypeSettings: {},
                parameterIds: [],
                scoreboardType: null,
                scoreboardSettings: null,
            });
            this.activeTabIndex = this.disciplineOverrideConfigs.length - 1;
        },

        removeOverride(index: number): void {
            this.disciplineOverrideConfigs.splice(index, 1);
            this.activeTabIndex = null;
        },

        updateOverrideName(index: number, name: string): void {
            this.disciplineOverrideConfigs[index].name = name;
        },

        addRange(): void {
            const constraints = this.activeConstraints;
            const lowerLimit = constraints.length > 0 ? constraints[constraints.length - 1].upperLimit + 1 : 2;
            const upperLimit = lowerLimit + 1;
            const fittingType = [...this.currentAllowedBracketTypes]
                .reverse()
                .find(t => constraintFitsRange({ lowerLimit, upperLimit, bracketType: t.bracketType }, [t]));
            const bracketType = fittingType?.bracketType ?? BracketTypeEnum.NotSupported;
            constraints.push(
                feedBracketConstraintWithDefaultSettings({
                    lowerLimit,
                    upperLimit,
                    bracketType,
                }),
            );
        },

        removeRange(index: number): void {
            const constraints = this.activeConstraints;
            const sizeBeforeDelete = constraints.length;
            constraints.splice(index, 1);
            if (constraints.length === 0) {
                return;
            }
            if (index === 0) {
                constraints[0].lowerLimit = 2;
                return;
            }
            if (sizeBeforeDelete === index - 1) {
                return;
            }
            this.adjustRanges(index - 1);
        },

        adjustRanges(index: number): void {
            const constraints = this.activeConstraints;
            if (index === constraints.length - 1) {
                return;
            }
            const current = constraints[index];
            const next = constraints[index + 1];
            next.lowerLimit = current.upperLimit + 1;
            if (next.upperLimit < next.lowerLimit) {
                next.upperLimit = next.lowerLimit + 1;
            }
        },

        applyPreset(constraints: BracketTypeConstraint[]): void {
            if (this.activeTabIndex === null) {
                this.disciplineDefaultConfig.bracketConstraints = JSON.parse(JSON.stringify(constraints));
            } else {
                this.disciplineOverrideConfigs[this.activeTabIndex].bracketConstraints = JSON.parse(JSON.stringify(constraints));
            }
        },

        save(): void {
            if (!this.selectedDiscipline) {
                return;
            }
            const defaultScoreboardType = this.disciplineDefaultConfig.scoreboardType!;
            const defaultScoreboardSettings = normalizeScoreboardSettings(
                defaultScoreboardType,
                this.disciplineDefaultConfig.scoreboardSettings,
            );
            this.saving = true;
            disciplinePackagesApi
                .updateDisciplinePackage(this.eventId, this.selectedDiscipline, {
                    scoreboardType: defaultScoreboardType,
                    scoreboardSettings: defaultScoreboardSettings,
                    bracketConstraints: this.disciplineDefaultConfig.bracketConstraints,
                    bracketTypeSettings: this.disciplineDefaultConfig.bracketTypeSettings,
                    parameterOverrides: this.disciplineOverrideConfigs.map(o => ({
                        name: o.name || null,
                        bracketConstraints: o.bracketConstraints,
                        bracketTypeSettings: o.bracketTypeSettings,
                        parameterIds: o.parameterIds,
                        scoreboardType: o.scoreboardType,
                        scoreboardSettings: o.scoreboardType ? normalizeScoreboardSettings(o.scoreboardType, o.scoreboardSettings) : null,
                    })),
                })
                .then(() => {
                    successToast(translate("discipline_packages.saved"));
                    const pkg = this.packages.find(p => p.discipline === this.selectedDiscipline);
                    if (pkg) {
                        pkg.isConfigured = true;
                        pkg.scoreboardType = defaultScoreboardType;
                        pkg.scoreboardSettings = { ...defaultScoreboardSettings };
                        pkg.bracketConstraints = JSON.parse(JSON.stringify(this.disciplineDefaultConfig.bracketConstraints));
                        pkg.bracketTypeSettings = { ...this.disciplineDefaultConfig.bracketTypeSettings };
                    }
                })
                .catch(handleError)
                .finally(() => {
                    this.saving = false;
                });
        },
    },
});
